import { DESTINATION_FILTER_RULES } from './config';
import type { DestinationFilterRule } from './config';

export type ExDestinationsWithRoutes = Record<string, string[]>;

/**
 * Finds the first matching filter rule for the given account ID.
 * Uses exact match comparison.
 *
 * @param accountId - The account ID to check
 * @param rules - Optional custom rules (defaults to DESTINATION_FILTER_RULES)
 * @returns The matching rule or null if no match
 */
export function findMatchingRule(
    accountId: string | undefined,
    rules: readonly DestinationFilterRule[] = DESTINATION_FILTER_RULES,
): DestinationFilterRule | null {
    if (!accountId) {
        return null;
    }
    return rules.find((rule) => rule.accountIds.includes(accountId)) ?? null;
}

/**
 * Filters destinations based on account ID.
 * Returns only allowed destinations if account matches a filter rule,
 * otherwise returns all destinations unchanged.
 *
 * @param destinations - The original destinations with their routes
 * @param accountId - The account ID to check (or undefined)
 * @param rules - Optional custom rules (defaults to DESTINATION_FILTER_RULES)
 * @returns Filtered destinations if account matches a rule, original otherwise
 */
export function filterDestinationsByAccount(
    destinations: ExDestinationsWithRoutes,
    accountId: string | undefined,
    rules: readonly DestinationFilterRule[] = DESTINATION_FILTER_RULES,
): ExDestinationsWithRoutes {
    const matchingRule = findMatchingRule(accountId, rules);

    if (!matchingRule) {
        return destinations;
    }

    // Filter to only include allowed destinations
    const filtered: ExDestinationsWithRoutes = {};
    for (const allowedDest of matchingRule.allowedDestinations) {
        if (allowedDest in destinations) {
            filtered[allowedDest] = destinations[allowedDest];
        }
    }

    // Safety fallback: if no allowed destinations exist in the schema,
    // return original to prevent breaking the widget
    if (Object.keys(filtered).length === 0) {
        return destinations;
    }

    return filtered;
}

/**
 * Checks if an account requires destination filtering.
 *
 * @param accountId - The account ID to check
 * @param rules - Optional custom rules (defaults to DESTINATION_FILTER_RULES)
 * @returns True if account matches a filter rule
 */
export function accountRequiresFiltering(
    accountId: string | undefined,
    rules: readonly DestinationFilterRule[] = DESTINATION_FILTER_RULES,
): boolean {
    return findMatchingRule(accountId, rules) !== null;
}

/**
 * Filters the executorsSchema to only include allowed destinations for the account.
 * Handles both enum and oneOf patterns in the exDestination property.
 *
 * @param schema - The executor schema from the API
 * @param accountId - The account ID to check (or undefined)
 * @param rules - Optional custom rules (defaults to DESTINATION_FILTER_RULES)
 * @returns Filtered schema if account matches a rule, original otherwise
 */
export function filterSchemaExDestinations<T extends Record<string, any>>(
    schema: T,
    accountId: string | undefined,
    rules: readonly DestinationFilterRule[] = DESTINATION_FILTER_RULES,
): T {
    const matchingRule = findMatchingRule(accountId, rules);
    if (!matchingRule) {
        return schema; // No filtering needed
    }

    const clonedSchema = structuredClone(schema);
    const exDestProp = clonedSchema.properties?.exDestination;

    if (!exDestProp) {
        return clonedSchema;
    }

    // Handle enum pattern
    if (Array.isArray(exDestProp.enum)) {
        const filteredEnum = exDestProp.enum.filter((dest: string) => matchingRule.allowedDestinations.includes(dest));
        // Only apply if we have matches, otherwise keep original (safety fallback)
        if (filteredEnum.length > 0) {
            exDestProp.enum = filteredEnum;
            // Update default if it's no longer in the enum
            if (exDestProp.default && !filteredEnum.includes(exDestProp.default)) {
                exDestProp.default = filteredEnum[0];
            }
        }
    }

    // Handle oneOf pattern
    if (Array.isArray(exDestProp.oneOf)) {
        const filteredOneOf = exDestProp.oneOf.filter((opt: any) =>
            matchingRule.allowedDestinations.includes(opt.const || opt.enum?.[0]),
        );
        if (filteredOneOf.length > 0) {
            exDestProp.oneOf = filteredOneOf;
        }
    }

    return clonedSchema;
}
